/**
 * fmt.* — NULL 렌더링 전용 공용 포매터 (최종지시서 ㉑ §3-7 · 2026-10-03).
 *
 * 규율: 값이 없으면 «null» 을 돌려준다. 자리표시(「-」·「—」·「미확인」·「0원」)를 만들지 않는다.
 *   호출부가 null 을 받으면 «그리지 않거나»(열·줄 생략) 빈 칸 + sr-only 「해당 없음」으로 처리한다.
 *
 * ⚠️ 이 모듈은 ㉑ §3-7 호출처에서만 쓴다(지시서). 기존 `lib/format.ts`·`lib/apt-format.ts` 의
 *    함수는 각자 자리표시를 돌려주는 계약이라 여기로 합치지 않는다 — 소비처 전부를 다시 재야 한다.
 */

import { kstNow } from '@/lib/cl-core/kst';
import { eokShort, eokShortNum } from '@/lib/cl-core/money';

type Num = number | string | null | undefined;

const toNum = (v: Num): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};

/** 양수만 값으로 친다(0·음수·NaN → null). 가격·세대수처럼 «0 이면 없는 것» 인 열 전용. */
const positive = (v: Num): number | null => {
  const n = toNum(v);
  return n !== null && n > 0 ? n : null;
};

/**
 * ㉗ C③ — 범위 구분자 «한 글자». 화면이 「4.9억–14.6억」(홈)·「4.9억 ~ 14.6억」(상세)·「10.12–10.14」(/apt)·
 *   「4.9~14.6억」(카드)로 네 꼴이었다. 실측(2026-10-06 렌더 경로 grep): 붙여 쓴 물결 18곳 · 띄어 쓴 물결 15곳 ·
 *   en dash 3곳 — 다수파이자 SEO 제목·정의문(title-tail·definition-lead·hub-lead)이 이미 쓰는 «붙여 쓴 물결» 로 모은다.
 *   ⚠️ `fmt.dash` 는 범위 물결을 건드리지 않는다는 계약이라 둘이 충돌하지 않는다(en dash 범위를 여기서 없앴다).
 */
export const RANGE_SEP = '~';

/** 범위의 한쪽이 «없는 값» 인가 — 기존 포매터들이 돌려주는 자리표시(`-`·`—`·`–`)도 없는 값으로 친다. */
const RANGE_EMPTY = new Set(['', '-', '—', '–', '―', 'undefined', 'null', 'NaN']);
const rangePart = (v: string | number | null | undefined | false): string | null => {
  if (v === null || v === undefined || v === false) return null;
  const s = String(v).trim();
  return RANGE_EMPTY.has(s) ? null : s;
};

/** 실거래 현장(trade·landmark)인가 — 가격 열이 분양가가 아니라 실거래 범위다. */
export const isTradeSiteType = (t: string | null | undefined): boolean => t === 'trade' || t === 'landmark';

// 만원 → 짧은 억 표기 «정본»(「6.9억」 · 버림 · 「.0」 제거). 구현은 cl-core/money 한 벌(Deno 공용) — 머리말 참조.
//   ⛔ 「N억 M만」 정확 표기는 lib/format/money 의 eokManLabel.
export { eokShort, eokShortNum };

export const fmt = {
  /**
   * ㉗ C③ — 범위 한 줄. «이미 포맷된» 양 끝을 받는다(가격·날짜 공용): `fmt.range(fmt.manwon(lo), fmt.manwon(hi))`.
   *   양쪽 다 있음 → `4.9억~14.6억` / 표기가 같음(min==max·반올림 후 동일) → 한 값 `7.9억`
   *   한쪽만 있음 → 그쪽만 / 둘 다 없음 → null(호출부가 조각을 통째로 뺀다 — 자리표시 금지).
   * ⚠️ 비교는 «표기» 로 한다 — 79,400 과 79,430 은 숫자는 달라도 둘 다 `7.9억` 이라 「7.9억~7.9억」이 된다.
   * ⚠️ 대소 정렬은 하지 않는다. 날짜 범위(시작~끝)는 순서가 뜻이다 — 숫자 뒤집힘은 호출부가 정한다.
   */
  range(
    lo: string | number | null | undefined | false,
    hi: string | number | null | undefined | false,
    sep: string = RANGE_SEP,
  ): string | null {
    const a = rangePart(lo);
    const b = rangePart(hi);
    if (a && b) return a === b ? a : `${a}${sep}${b}`;
    return a ?? b ?? null;
  },

  /**
   * 분양가 «바닥부터» 한 줄 — 범위 대신 하한을 앞세운다(2026-10-24 Node: 「분양가는 최고가보다 최저가로」).
   *   양쪽 다 있음(표기가 다름) → `6.9억~`(style 'word' → `6.9억부터`) / 표기가 같음 → `6.9억` / 한쪽만 → 그쪽 값 / 둘 다 없음 → null.
   *   «이미 포맷된» 양 끝을 받는다 — `fmt.range` 와 같은 계약이다(자리표시 `-`·`—` 는 없는 값).
   * ⚠️ 여기서 「최저」라는 말을 붙이지 않는다. `apt_sites.price_min` 은 «가장 작은 타입의 최고층가» 라
   *    진짜 최저가(`lttot_min_amount`)가 아닐 수 있다 — 「6.9억~」 은 «이 값부터 있다» 까지만 말한다.
   * ⛔ 분양가·공급가 전용이다. 실거래 범위·날짜 범위는 계속 `fmt.range` 를 쓴다(뜻이 다르다).
   */
  priceFrom(
    lo: string | number | null | undefined | false,
    hi: string | number | null | undefined | false,
    style: 'tilde' | 'word' = 'tilde',
  ): string | null {
    const a = rangePart(lo);
    const b = rangePart(hi);
    // 'word' = 문장 안(「분양가는 6.9억부터입니다」) — 「6.9억~입니다」는 읽기 어렵다.
    if (a && b) return a === b ? a : style === 'word' ? `${a}부터` : `${a}${RANGE_SEP}`;
    return a ?? b ?? null;
  },

  /**
   * 현장 가격 한 줄 — `site_type` 으로 뜻을 가른다(2026-10-24).
   *   실거래·랜드마크(`trade`·`landmark`) → `price_min/max` 가 실거래 범위라 `fmt.range`(「2.7억~4.8억」) 그대로.
   *   그 밖(분양·미분양·재개발) → 분양가라 `fmt.priceFrom`(「6.9억~」).
   * ⚠️ 판정 키는 site-faqs `tradeSite` 와 같다(trade·landmark) — 두 벌로 갈리지 않게 여기 한 곳.
   */
  sitePrice(
    lo: string | number | null | undefined | false,
    hi: string | number | null | undefined | false,
    siteType: string | null | undefined,
  ): string | null {
    return isTradeSiteType(siteType) ? fmt.range(lo, hi) : fmt.priceFrom(lo, hi);
  },

  /**
   * 연월 → `YYYY.MM`. `202509`·`'202509'`·`'2025-09'`·`'2025.9'` 를 받는다.
   * 그 밖의 모양(전체 날짜·자유 문구·빈 값)은 null — 호출부가 원문을 그대로 쓸지 정한다.
   */
  ym(v: Num): string | null {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    const m = /^(\d{4})(\d{2})$/.exec(s) ?? /^(\d{4})[-./](\d{1,2})$/.exec(s);
    if (!m) return null;
    const mm = Number(m[2]);
    if (mm < 1 || mm > 12) return null;
    return `${m[1]}.${String(mm).padStart(2, '0')}`;
  },

  /** 만원 단위 금액 → `36억` / `6.9억` / `7,672만`. 0·결측 → null. 표기 정본은 아래 `eokShort`(버림·「.0」 제거). */
  manwon(v: Num): string | null {
    return eokShort(v);
  },

  /** 원 단위 금액 → `1,234원`. 결측 → null. ⚠️ 0 은 «값» 이다(세액 0원 등) — 호출부가 판단. */
  won(v: Num): string | null {
    const n = toNum(v);
    if (n === null) return null;
    return `${Math.round(n).toLocaleString('ko-KR')}원`;
  },

  /** 세대수 → `1,234세대`. 0·결측 → null(「0세대」 금지). */
  households(v: Num): string | null {
    const n = positive(v);
    return n === null ? null : `${Math.round(n).toLocaleString('ko-KR')}세대`;
  },

  /** 숫자 + 단위. 0·결측 → null. (예: 준공 2011 → `2011`, 전세가율 50 → `50%`, 평당 11447 group → `11,447만`) */
  unit(v: Num, suffix = '', group = false): string | null {
    const n = positive(v);
    return n === null ? null : `${group ? n.toLocaleString('ko-KR') : String(n)}${suffix}`;
  },

  /**
   * 조각 이어 붙이기 — 빈 조각(null·undefined·''·공백·'undefined'·'null')을 버리고 한 칸 공백으로 잇는다.
   * og:image:alt · geo.placename 처럼 «조각 중 일부가 비는» 문자열 전용. 전부 비면 ''.
   */
  join(...parts: Array<string | number | null | undefined | false>): string {
    return parts
      .map((p) => (p === null || p === undefined || p === false ? '' : String(p).trim()))
      .filter((p) => p !== '' && p !== 'undefined' && p !== 'null' && p !== 'NaN')
      .join(' ')
      .replace(/\s{2,}/g, ' ');
  },

  /**
   * ㉓ A① — 시각(ISO·Date) → KST `YYYY.MM.DD`. 결측·파싱 불가 → null(「Invalid Date」 금지).
   * ⚠️ «오늘» 을 만들지 않는다 — 호출부가 데이터의 시각(updated_at 등)을 넘긴다.
   */
  date(v: string | Date | null | undefined): string | null {
    if (v === null || v === undefined || v === '') return null;
    const t = v instanceof Date ? v.getTime() : Date.parse(String(v));
    if (!Number.isFinite(t)) return null;
    const k = kstNow(t);
    return `${k.getUTCFullYear()}.${String(k.getUTCMonth() + 1).padStart(2, '0')}.${String(k.getUTCDate()).padStart(2, '0')}`;
  },

  /** ㉓ A① — 개수 → `1,234곳`. 0·결측 → null(「0곳」 문장 금지). */
  count(v: Num, suffix = '곳'): string | null {
    const n = positive(v);
    return n === null ? null : `${Math.round(n).toLocaleString('ko-KR')}${suffix}`;
  },

  /**
   * ㉔ C — 정의문·FAQ 문장 안의 «구분 대시» 를 한 꼴(` — `, 앞뒤 한 칸 em dash)로 맞춘다.
   *   `A — B`·`A – B`·`A―B`·`A—B`·`A - B`(앞뒤 공백 있는 하이픈) → `A — B`.
   *   ⚠️ 공백 없는 하이픈(`2-1구역`·`D-01`)과 범위 물결(`9.0억~12.4억`)은 건드리지 않는다.
   *   ⚠️ 문장 맨 앞·맨 끝에 걸린 대시(빈 조각 자리표시)는 지운다 — 「—」 단독 자리표시 금지.
   */
  dash(s: string | null | undefined): string {
    if (s === null || s === undefined) return '';
    return String(s)
      .replace(/\s*[—–―]+\s*/g, ' — ')
      .replace(/\s+-+\s+/g, ' — ')
      .replace(/^\s*—\s*/, '')
      .replace(/\s*—\s*$/, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  },

  /** 「0원」 류 금액 문자열인가 — 계산기 내역 줄 생략 판정 전용. (`0원`·`0만원`·`-0원`·`0 원`) */
  isZeroAmount(s: string | null | undefined): boolean {
    if (!s) return false;
    return /^[-−+]?\s*0(?:\.0+)?\s*(?:원|만\s*원|만|억\s*원|억)$/.test(String(s).trim());
  },
};

/**
 * ㉗ C — 낱개 내보내기. `fmt` 라는 이름을 이미 지역 함수로 쓰는 파일(AptMainGrid·card-format·(main)/page 등)이
 *   충돌 없이 부르는 통로다. ⚠️ 구현은 위 객체의 메서드 «그대로» 다 — 두 벌이 아니다(this 미사용).
 */
export const fmtRange = fmt.range;
export const fmtYm = fmt.ym;
export const fmtPriceFrom = fmt.priceFrom;
export const fmtSitePrice = fmt.sitePrice;

export default fmt;
